import { useState, type FC } from "react";
import { useMutation } from "@tanstack/react-query";
import type { SxProps, Theme } from "@mui/material/styles";
import { Translate, useTranslation } from "@privacy/translations";
import {
  acknowledgeOwnCopyMutationOptions,
  removeCustomerLibraryProcessingFromOrganisationMutationOptions,
} from "../api/registrations";
import type { RegistrationById } from "../api/registrations.types";
import { usePermission } from "../shared/permissions";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { useHasPermissionToChooseResponsible } from "../pages/RegistrationsGridPage/RegistrationDrawer/useHasPermissionToUpdate";
import { ConfirmDeleteDialog } from "../shared/ConfirmDeleteDialog";
import { Strong } from "../shared/Strong";
import { DecisionNotice } from "./DecisionNotice";
import { OwnCopyNotice } from "./OwnCopyNotice";
import { SharedProcessingWarning } from "./SharedProcessingWarning";

export const SharedProcessingAlert: FC<{ registration: RegistrationById; sx?: SxProps<Theme> }> = ({
  registration,
  sx,
}) => {
  const { t } = useTranslation();
  const hasPermissionToChooseResponsible = useHasPermissionToChooseResponsible();
  const editability = registration.organisationEditability;

  if (editability?.isCustomerManaged && editability.managedBy) {
    if (editability.awaitingClaim) return <ClaimNotice registration={registration} sx={sx} />;

    return registration.responsible ? null : (
      <SharedProcessingWarning sx={sx}>
        {t(
          hasPermissionToChooseResponsible(registration)
            ? "RegistrationDrawer.Responsible.MissingForOrganisation"
            : "RegistrationDrawer.Responsible.MissingForOrganisation.NoPermission",
          { customer: editability.managedBy.name }
        )}
      </SharedProcessingWarning>
    );
  }

  if (registration.ownCopy && !registration.ownCopy.acknowledgedAt) {
    return <OwnCopyNotice registration={registration} ownCopy={registration.ownCopy} sx={sx} />;
  }

  return null;
};

const ClaimNotice: FC<{ registration: RegistrationById; sx?: SxProps<Theme> }> = ({ registration, sx }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const { navigate, paths } = usePrivacyNavigate();
  const hasPermissionToChooseResponsible = useHasPermissionToChooseResponsible();
  const claimMutation = useMutation(acknowledgeOwnCopyMutationOptions());
  const removeMutation = useMutation(removeCustomerLibraryProcessingFromOrganisationMutationOptions());
  const [isConfirmingNo, setIsConfirmingNo] = useState(false);
  const customer = registration.organisationEditability?.managedBy?.name ?? "";
  const canAnswer = hasPermissionToChooseResponsible(registration);
  const canAnswerNo = hasPermission("nova-forms-api:registration:delete", registration.permissions);

  return (
    <>
      <DecisionNotice
        sx={sx}
        eyebrow={
          <Translate translationKey="SharedProcessing.Claim.What" values={{ customer: <Strong>{customer}</Strong> }} />
        }
        question={t("SharedProcessing.Claim.Question")}
      >
        {canAnswer && (
          <DecisionNotice.Answer
            answer="yes"
            consequence={t("SharedProcessing.Claim.IfYes", { customer })}
            isPending={claimMutation.isPending}
            blockedReason={registration.responsible ? undefined : t("SharedProcessing.Claim.ChooseResponsibleFirst")}
            onClick={() => claimMutation.mutate(registration.id)}
          />
        )}
        {canAnswerNo && (
          <DecisionNotice.Answer
            answer="no"
            consequence={t("SharedProcessing.Claim.IfNo", { customer })}
            onClick={() => setIsConfirmingNo(true)}
          />
        )}
        {!canAnswer && !canAnswerNo && <DecisionNotice.NoPermission />}
        {(claimMutation.isError || removeMutation.isError) && <DecisionNotice.Error />}
      </DecisionNotice>
      <ConfirmDeleteDialog
        isOpen={isConfirmingNo}
        onClose={() => setIsConfirmingNo(false)}
        headerText={t("SharedProcessing.Claim.ConfirmNo")}
        message={
          <Translate
            translationKey="RegistrationDrawer.RemoveFromRegister.ConfirmMessage"
            values={{ name: <Strong>{registration.name}</Strong>, customer: <Strong>{customer}</Strong> }}
          />
        }
        confirmButtonLabel={t("RegistrationDrawer.RemoveFromRegister.Confirm")}
        onDelete={() =>
          removeMutation.mutateAsync(registration.id, {
            onSuccess: () => navigate(paths.registrationsGrid({ variant: "Controller" })),
          })
        }
      />
    </>
  );
};
